import { Tooltip } from '@mui/material';
import { FC, ReactElement, ReactNode, useLayoutEffect, useRef, useState, cloneElement } from 'react';

interface TooltipComponentProps {
  children: ReactElement;
  tooltipTitle: ReactNode;
  lineClamp?: number;
}

/**
 * TooltipComponent displays a tooltip when the content is overflowing (single or multi-line).
 * It injects the required styles to the child element for overflow detection (you can override it by passing a style prop to the child element).
 * For single-line (lineClamp=1) it uses scrollWidth vs clientWidth; for multi-line it uses scrollHeight vs clientHeight.
 * @param children - The child element to display the tooltip for.
 * @param tooltipTitle - The title of the tooltip.
 * @param lineClamp - Max lines to show before ellipsis (default: 1 for single-line).
 * @returns A tooltip component.
 */
export const TooltipComponent: FC<TooltipComponentProps> = ({ children, tooltipTitle, lineClamp = 1 }) => {
  const contentRef = useRef<HTMLElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const isSingleLine = lineClamp === 1;

  useLayoutEffect(() => {
    if (contentRef.current) {
      const el = contentRef.current;
      if (isSingleLine) {
        setIsOverflowing((el.scrollWidth ?? 0) > (el.clientWidth ?? 0));
      } else {
        setIsOverflowing((el.scrollHeight ?? 0) > (el.clientHeight ?? 0));
      }
    }
  }, [children, isSingleLine]);

  const finalTooltipTitle = isOverflowing ? tooltipTitle : '';

  const baseStyle = {
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    ...(children.props.style || {})
  };
  const overflowStyle = isSingleLine
    ? { display: 'inline-block', whiteSpace: 'nowrap' as const }
    : {
        display: '-webkit-box',
        WebkitLineClamp: lineClamp,
        WebkitBoxOrient: 'vertical' as const
      };

  const childWithRef = children
    ? cloneElement(children, {
        ref: contentRef,
        style: { ...baseStyle, ...overflowStyle }
      })
    : children;

  return <Tooltip title={finalTooltipTitle}>{childWithRef}</Tooltip>;
};
